// Couche de mapping du Bloc 6 : contrats, loyers, paiements, dépenses.
//
// Supabase est la source de vérité. Les tables réelles utilisent des enums
// techniques (`a_venir`, `en_retard`, `orange_money`, ...) alors que
// l'interface conserve des libellés métier. Ce module centralise les
// correspondances afin d'éviter toute divergence frontend/DB.
//
// Tables / vues utilisées :
//   - contrats
//   - loyers + v_loyers_details + v_impayes
//   - paiements + v_paiements_details
//   - depenses
//
// RPC : generer_loyer_contrat(), generer_loyers_periode(), recalculer_statut_loyer()

import type { PlatformContrat, PlatformScope } from "./types";

/* ------------------------------------------------------------------ */
/* Enums techniques de la base                                         */
/* ------------------------------------------------------------------ */

export type DbContratStatut = "actif" | "a_venir" | "termine" | "resilie" | "suspendu";
export type DbLoyerStatut = "a_payer" | "partiel" | "paye" | "en_retard" | "annule";
export type DbModePaiement = "especes" | "orange_money" | "moov_money" | "virement" | "cheque" | "autre";
export type DbDepenseCategorie =
  | "entretien"
  | "reparation"
  | "electricite"
  | "eau"
  | "securite"
  | "nettoyage"
  | "taxe"
  | "assurance"
  | "autre";

/* ------------------------------------------------------------------ */
/* Modèles exposés au frontend (statuts « métier »)                    */
/* ------------------------------------------------------------------ */

export type PlatformLoyerStatut = "paye" | "a-venir" | "en-retard" | "impaye" | "annule";

export type PlatformModePaiement = "especes" | "orange-money" | "moov-money" | "virement" | "cheque" | "autre";

export type PlatformDepenseCategorie =
  | "reparation"
  | "electricite"
  | "eau"
  | "nettoyage"
  | "securite"
  | "entretien"
  | "taxe"
  | "assurance"
  | "autre";

/** Au-delà de ce nombre de jours de retard, un loyer passe de « En retard » à « Impayé ». */
export const IMPAYE_SEUIL_JOURS = 30;

export interface PlatformLoyer {
  id: string;
  scope: PlatformScope;
  contratId: string;
  proprietaireId: string;
  immeubleId: string;
  bienId: string;
  locataireId: string;
  /** Période au format `YYYY-MM`. */
  periode: string;
  montant: number;
  totalPaye: number;
  reste: number;
  /** Date d'échéance `YYYY-MM-DD`. */
  echeance: string;
  statut: PlatformLoyerStatut;
  joursRetard: number;
}

export interface PlatformPaiement {
  id: string;
  scope: PlatformScope;
  contratId: string;
  proprietaireId: string;
  immeubleId: string;
  bienId: string;
  locataireId: string;
  /** Période du loyer concerné, format `YYYY-MM`. */
  periode: string;
  montant: number;
  /** Date du paiement `YYYY-MM-DD`. */
  date: string;
  mode: PlatformModePaiement;
  reference: string;
  note: string;
  createdAt: string;
}

export interface PlatformDepense {
  id: string;
  scope: PlatformScope;
  proprietaireId: string;
  immeubleId: string;
  bienId: string;
  libelle: string;
  categorie: PlatformDepenseCategorie;
  montant: number;
  date: string;
  description: string;
  justificatif: string;
  createdAt: string;
}

export interface PlatformPaiementInput {
  contratId: string;
  periode: string;
  montant: number;
  date: string;
  mode: PlatformModePaiement;
  reference: string;
  note: string;
}

export interface PlatformDepenseInput {
  immeubleId: string;
  bienId: string;
  libelle: string;
  categorie: PlatformDepenseCategorie;
  montant: number;
  date: string;
  description: string;
  justificatif: string;
}

/* ------------------------------------------------------------------ */
/* Correspondances enums DB <-> frontend                               */
/* ------------------------------------------------------------------ */

const CONTRAT_STATUT_TO_DB: Record<string, DbContratStatut> = {
  brouillon: "a_venir",
  actif: "actif",
  a_venir: "a_venir",
  expire: "termine",
  termine: "termine",
  resilie: "resilie",
  suspendu: "suspendu",
};

const CONTRAT_STATUT_FROM_DB: Record<string, PlatformContrat["statut"]> = {
  a_venir: "brouillon",
  actif: "actif",
  termine: "expire",
  resilie: "resilie",
  suspendu: "suspendu",
};

export function contratStatutToDb(statut: string): DbContratStatut {
  return CONTRAT_STATUT_TO_DB[statut] ?? "a_venir";
}

export function contratStatutFromDb(statut: string): PlatformContrat["statut"] {
  return CONTRAT_STATUT_FROM_DB[statut] ?? "brouillon";
}

const MODE_TO_DB: Record<string, DbModePaiement> = {
  especes: "especes",
  "orange-money": "orange_money",
  "moov-money": "moov_money",
  virement: "virement",
  cheque: "cheque",
  autre: "autre",
};

const MODE_FROM_DB: Record<string, PlatformModePaiement> = {
  especes: "especes",
  orange_money: "orange-money",
  moov_money: "moov-money",
  virement: "virement",
  cheque: "cheque",
  autre: "autre",
};

export function modePaiementToDb(mode: string): DbModePaiement {
  return MODE_TO_DB[mode] ?? "autre";
}

export function modePaiementFromDb(mode: string): PlatformModePaiement {
  return MODE_FROM_DB[mode] ?? "autre";
}

const CATEGORIE_TO_DB: Record<string, DbDepenseCategorie> = {
  reparation: "reparation",
  electricite: "electricite",
  eau: "eau",
  nettoyage: "nettoyage",
  securite: "securite",
  entretien: "entretien",
  taxe: "taxe",
  assurance: "assurance",
  autre: "autre",
};

export function depenseCategorieToDb(categorie: string): DbDepenseCategorie {
  return CATEGORIE_TO_DB[categorie] ?? "autre";
}

export function depenseCategorieFromDb(categorie: string): PlatformDepenseCategorie {
  const value = categorie as PlatformDepenseCategorie;
  return value in CATEGORIE_TO_DB ? value : "autre";
}

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

export type Row = Record<string, unknown>;

export const str = (value: unknown): string => (typeof value === "string" ? value : "");
export const num = (value: unknown): number => {
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};
export const nullable = (value: string): string | null => (value && value.trim() ? value : null);

/** `YYYY-MM-DD` → `YYYY-MM`. */
export function toMonthKey(date: string): string {
  return (date ?? "").slice(0, 7);
}

/** Premier jour du mois au format `YYYY-MM-DD` (attendu par les RPC loyers). */
export function monthFirstDay(periode: string): string {
  return `${periode}-01`;
}

export function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

export function daysBetween(fromISO: string, toISO: string): number {
  const from = new Date(`${fromISO}T00:00:00`).getTime();
  const to = new Date(`${toISO}T00:00:00`).getTime();
  if (Number.isNaN(from) || Number.isNaN(to)) return 0;
  return Math.round((to - from) / 86_400_000);
}

/** Statut frontend d'un loyer à partir du statut calculé par la vue Supabase. */
export function loyerStatutFromDb(statutCalcule: string, dateEcheance: string): PlatformLoyerStatut {
  const today = todayISO();
  switch (statutCalcule) {
    case "paye":
      return "paye";
    case "annule":
      return "a-venir";
    case "partiel":
      return "en-retard";
    case "en_retard": {
      const retard = dateEcheance ? daysBetween(dateEcheance, today) : 0;
      return retard > IMPAYE_SEUIL_JOURS ? "impaye" : "en-retard";
    }
    case "a_payer":
    default:
      return "a-venir";
  }
}

export function loyerJoursRetard(statutCalcule: string, dateEcheance: string): number {
  if (statutCalcule === "paye" || statutCalcule === "annule" || !dateEcheance) return 0;
  const retard = daysBetween(dateEcheance, todayISO());
  return retard > 0 ? retard : 0;
}

/* ------------------------------------------------------------------ */
/* Mappers lignes -> modèles                                           */
/* ------------------------------------------------------------------ */

export interface ImmeubleScopeInfo {
  scope: PlatformScope;
  proprietaireId: string;
}

/** Contrat : la portée et le propriétaire sont déduits de l'immeuble du bien. */
export function mapContratRow(
  row: Row,
  bienToImmeuble: Map<string, string>,
  immeubleInfo: Map<string, ImmeubleScopeInfo>
): PlatformContrat {
  const bienId = str(row.bien_id);
  const immeubleId = bienToImmeuble.get(bienId) ?? "";
  const info = immeubleInfo.get(immeubleId);
  return {
    id: str(row.id),
    scope: info?.scope ?? "proprietaire",
    proprietaireId: info?.proprietaireId ?? "",
    immeubleId,
    bienId,
    locataireId: str(row.locataire_id),
    dateDebut: str(row.date_debut),
    dateFin: str(row.date_fin),
    loyerMensuel: num(row.loyer_mensuel),
    caution: num(row.caution),
    avance: num(row.avance),
    jourEcheance: num(row.jour_echeance),
    conditionsParticulieres: str(row.conditions_particulieres),
    document: str(row.document),
    statut: contratStatutFromDb(str(row.statut)),
    createdAt: str(row.created_at),
  };
}

/** Loyer : à partir d'une ligne de `v_loyers_details`. */
export function mapLoyerRow(
  row: Row,
  immeubleInfo: Map<string, ImmeubleScopeInfo>,
  contratOwner: Map<string, string>
): PlatformLoyer {
  const contratId = str(row.contrat_id);
  const immeubleId = str(row.immeuble_id);
  const info = immeubleInfo.get(immeubleId);
  const statutCalcule = str(row.statut_calcule) || str(row.statut);
  const dateEcheance = str(row.date_echeance);
  const montant = num(row.montant_du);
  const totalPaye = num(row.montant_paye);
  return {
    id: str(row.id),
    scope: info?.scope ?? "proprietaire",
    contratId,
    proprietaireId: contratOwner.get(contratId) ?? info?.proprietaireId ?? "",
    immeubleId,
    bienId: str(row.bien_id),
    locataireId: str(row.locataire_id),
    periode: toMonthKey(str(row.periode)),
    montant,
    totalPaye,
    reste: num(row.reste_a_payer),
    echeance: dateEcheance,
    statut: loyerStatutFromDb(statutCalcule, dateEcheance),
    joursRetard: loyerJoursRetard(statutCalcule, dateEcheance),
  };
}

/** Paiement : à partir d'une ligne de `v_paiements_details`. */
export function mapPaiementRow(
  row: Row,
  immeubleInfo: Map<string, ImmeubleScopeInfo>,
  contratOwner: Map<string, string>
): PlatformPaiement {
  const contratId = str(row.contrat_id);
  const immeubleId = str(row.immeuble_id);
  const info = immeubleInfo.get(immeubleId);
  return {
    id: str(row.id),
    scope: info?.scope ?? "proprietaire",
    contratId,
    proprietaireId: contratOwner.get(contratId) ?? info?.proprietaireId ?? "",
    immeubleId,
    bienId: str(row.bien_id),
    locataireId: str(row.locataire_id),
    periode: toMonthKey(str(row.periode)),
    montant: num(row.montant),
    date: str(row.date_paiement),
    mode: modePaiementFromDb(str(row.mode_paiement)),
    reference: str(row.reference),
    note: str(row.commentaire),
    createdAt: str(row.created_at),
  };
}

/** Dépense : la portée est déduite de l'immeuble ou du bien. */
export function mapDepenseRow(
  row: Row,
  immeubleInfo: Map<string, ImmeubleScopeInfo>,
  bienToImmeuble: Map<string, string>
): PlatformDepense {
  let immeubleId = str(row.immeuble_id);
  if (!immeubleId && str(row.bien_id)) {
    immeubleId = bienToImmeuble.get(str(row.bien_id)) ?? "";
  }
  const info = immeubleInfo.get(immeubleId);
  return {
    id: str(row.id),
    scope: info?.scope ?? "proprietaire",
    proprietaireId: info?.proprietaireId ?? "",
    immeubleId,
    bienId: str(row.bien_id),
    libelle: str(row.libelle),
    categorie: depenseCategorieFromDb(str(row.categorie)),
    montant: num(row.montant),
    date: str(row.date_depense),
    description: str(row.description),
    justificatif: str(row.document),
    createdAt: str(row.created_at),
  };
}